"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";

const COLORS = [
    { key: "yellow", bg: "bg-yellow-400", ring: "ring-yellow-400" },
    { key: "blue", bg: "bg-blue-400", ring: "ring-blue-400" },
    { key: "green", bg: "bg-green-400", ring: "ring-green-400" },
    { key: "pink", bg: "bg-pink-400", ring: "ring-pink-400" },
];

const HIGHLIGHT_COLORS = {
    light: {
        yellow: { bg: "rgba(250, 204, 21, 0.35)", line: "rgba(250, 204, 21, 0.6)" },
        blue: { bg: "rgba(96, 165, 250, 0.3)", line: "rgba(96, 165, 250, 0.55)" },
        green: { bg: "rgba(74, 222, 128, 0.3)", line: "rgba(74, 222, 128, 0.55)" },
        pink: { bg: "rgba(244, 114, 182, 0.3)", line: "rgba(244, 114, 182, 0.55)" },
    },
    dark: {
        yellow: { bg: "rgba(250, 204, 21, 0.4)", line: "rgba(250, 204, 21, 0.75)" },
        blue: { bg: "rgba(96, 165, 250, 0.35)", line: "rgba(96, 165, 250, 0.65)" },
        green: { bg: "rgba(74, 222, 128, 0.35)", line: "rgba(74, 222, 128, 0.65)" },
        pink: { bg: "rgba(244, 114, 182, 0.35)", line: "rgba(244, 114, 182, 0.65)" },
    },
};

function isDarkMode() {
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("dark");
}

function getColors(color) {
    const mode = isDarkMode() ? "dark" : "light";
    return HIGHLIGHT_COLORS[mode][color] || HIGHLIGHT_COLORS[mode].yellow;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function extractRangeText(range, container) {
    if (!range || !container) return "";
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let result = "";
    let node;
    while ((node = walker.nextNode())) {
        if (!range.intersectsNode(node)) continue;
        const text = node.textContent;
        let start = 0;
        let end = text.length;
        if (node === range.startContainer) start = range.startOffset;
        if (node === range.endContainer) end = range.endOffset;
        result += text.slice(start, end);
    }
    return result;
}

function findTextRanges(container, text) {
    if (!text || !container) return [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let total = 0;
    let node;
    while ((node = walker.nextNode())) {
        nodes.push({ node, start: total, length: node.textContent.length });
        total += node.textContent.length;
    }
    const fullText = nodes.map((n) => n.node.textContent).join("");
    const idx = fullText.indexOf(text);
    if (idx === -1) return [];
    const endIdx = idx + text.length;
    try {
        const range = document.createRange();
        let startSet = false;
        for (const n of nodes) {
            const nEnd = n.start + n.length;
            if (!startSet && idx < nEnd) {
                range.setStart(n.node, idx - n.start);
                startSet = true;
            }
            if (startSet && endIdx <= nEnd) {
                range.setEnd(n.node, endIdx - n.start);
                return [range];
            }
        }
        if (startSet && nodes.length > 0) {
            const last = nodes[nodes.length - 1];
            range.setEnd(last.node, last.length);
            return [range];
        }
    } catch { return []; }
    return [];
}

function applyHighlights(container, notes, rangeMapRef) {
    if (typeof CSS === "undefined" || !CSS.highlights) return;
    CSS.highlights.delete("note-preview");
    CSS.highlights.delete("note-yellow");
    CSS.highlights.delete("note-blue");
    CSS.highlights.delete("note-green");
    CSS.highlights.delete("note-pink");
    if (!container || !notes.length) { rangeMapRef.current = {}; return; }
    const buckets = { yellow: [], blue: [], green: [], pink: [] };
    const newRangeMap = {};
    for (const note of notes) {
        if (!note.highlightedText) continue;
        const color = note.color || "yellow";
        const ranges = findTextRanges(container, note.highlightedText);
        if (ranges.length > 0) {
            buckets[color]?.push(...ranges);
            newRangeMap[note.id] = ranges[0];
        }
    }
    for (const [color, ranges] of Object.entries(buckets)) {
        if (ranges.length > 0) CSS.highlights.set(`note-${color}`, new Highlight(...ranges));
    }
    rangeMapRef.current = newRangeMap;
}

// Inject ::highlight() styles at runtime (Turbopack can't parse them statically)
const STYLES_ID = "article-highlight-styles";
function buildHighlightCSS() {
    const mode = isDarkMode() ? "dark" : "light";
    const colors = HIGHLIGHT_COLORS[mode];
    return Object.entries(colors)
        .map(([name, c]) => `::highlight(note-${name}){background-color:${c.bg};text-decoration:underline 2px ${c.line};text-underline-offset:3px}`)
        .join("\n");
}

function syncStyles() {
    if (typeof document === "undefined") return;
    let style = document.getElementById(STYLES_ID);
    if (!style) { style = document.createElement("style"); style.id = STYLES_ID; document.head.appendChild(style); }
    style.textContent = buildHighlightCSS();
}

function setPreviewStyle(color) {
    const c = getColors(color);
    let style = document.getElementById("note-preview-style");
    if (!style) { style = document.createElement("style"); style.id = "note-preview-style"; document.head.appendChild(style); }
    style.textContent = `::highlight(note-preview){background-color:${c.bg};text-decoration:underline 2px ${c.line};text-underline-offset:3px}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ArticleHighlighter({ articleId, children }) {
    const { data: session } = useSession();
    const t = useTranslations();
    const contentRef = useRef(null);
    const rangeMapRef = useRef({});

    const [notes, setNotes] = useState([]);
    const [selectionPopover, setSelectionPopover] = useState(null);
    const [highlightPopover, setHighlightPopover] = useState(null);

    // Inject styles and re-sync when dark mode toggles
    useEffect(() => {
        syncStyles();
        const observer = new MutationObserver(() => syncStyles());
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    // API
    const loadNotes = useCallback(async () => {
        if (!articleId) return;
        try {
            const res = await fetch("/api/articles/notes", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "list", articleId }),
            });
            if (!res.ok) return;
            const data = await res.json();
            setNotes(data.notes || []);
        } catch {}
    }, [articleId]);

    const createHighlight = async (highlightedText, color) => {
        if (!highlightedText) return;
        try {
            const res = await fetch("/api/articles/notes", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "create",
                    articleId,
                    content: highlightedText,
                    color: color || "yellow",
                    highlightedText,
                }),
            });
            const data = await res.json();
            if (res.ok) setNotes(data.notes || []);
            else toast.error(data.message);
        } catch {
            toast.error(t("article_note_error"));
        }
    };

    const deleteHighlight = async (noteId) => {
        try {
            const res = await fetch("/api/articles/notes", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "delete", articleId, noteId }),
            });
            const data = await res.json();
            if (res.ok) setNotes(data.notes || []);
            else toast.error(data.message);
        } catch {
            toast.error(t("article_note_error"));
        }
    };

    const updateHighlightColor = async (noteId, color) => {
        // Optimistic update
        setNotes((prev) => prev.map((n) => n.id === noteId ? { ...n, color } : n));
        try {
            const res = await fetch("/api/articles/notes", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "update", articleId, noteId, color }),
            });
            const data = await res.json();
            if (res.ok) setNotes(data.notes || []);
        } catch {}
    };

    // Load on mount
    useEffect(() => { if (session) loadNotes(); }, [session, loadNotes]);

    // Apply highlights
    useEffect(() => {
        const timer = setTimeout(() => applyHighlights(contentRef.current, notes, rangeMapRef), 150);
        return () => clearTimeout(timer);
    }, [notes]);

    // Cleanup on unmount
    useEffect(() => () => {
        if (typeof CSS !== "undefined" && CSS.highlights) {
            ["note-preview", "note-yellow", "note-blue", "note-green", "note-pink"].forEach((n) => CSS.highlights.delete(n));
        }
    }, []);

    // Text selection → color picker popover
    useEffect(() => {
        const container = contentRef.current;
        if (!container || !session) return;
        const handleMouseUp = (e) => {
            if (e.target.closest("[data-note-popover]")) return;
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed || !selection.toString().trim()) return;
            const range = selection.getRangeAt(0);
            if (!container.contains(range.commonAncestorContainer)) return;
            const rawText = extractRangeText(range, container);
            if (!rawText) return;
            const previewRange = range.cloneRange();
            const rect = range.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            selection.removeAllRanges();
            setHighlightPopover(null);
            setSelectionPopover({
                text: rawText,
                previewRange,
                top: rect.bottom - containerRect.top + 8,
                left: Math.min(Math.max(0, rect.left - containerRect.left), containerRect.width - 180),
            });
        };
        container.addEventListener("mouseup", handleMouseUp);
        return () => container.removeEventListener("mouseup", handleMouseUp);
    }, [session]);

    // Click on existing highlight → delete popover
    useEffect(() => {
        const container = contentRef.current;
        if (!container || !session) return;
        const handleClick = (e) => {
            if (e.target.closest("[data-note-popover]")) return;
            if (selectionPopover) return;
            const caretRange = document.caretRangeFromPoint?.(e.clientX, e.clientY) || document.caretPositionFromPoint?.(e.clientX, e.clientY);
            if (!caretRange) return;
            const clickNode = caretRange.startContainer || caretRange.offsetNode;
            const clickOffset = caretRange.startOffset || caretRange.offset;
            for (const note of notes) {
                if (!note.highlightedText) continue;
                const storedRange = rangeMapRef.current[note.id];
                if (!storedRange) continue;
                try {
                    if (!storedRange.isPointInRange(clickNode, clickOffset)) continue;
                    // Check that the click is actually on the visible text pixels, not empty block space
                    const rects = storedRange.getClientRects();
                    let onText = false;
                    for (const r of rects) {
                        if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
                            onText = true;
                            break;
                        }
                    }
                    if (!onText) continue;
                    const rect = storedRange.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    setSelectionPopover(null);
                    setHighlightPopover({
                        note,
                        top: rect.bottom - containerRect.top + 8,
                        left: Math.min(Math.max(0, rect.left - containerRect.left), containerRect.width - 180),
                    });
                    return;
                } catch {}
            }
        };
        container.addEventListener("click", handleClick);
        return () => container.removeEventListener("click", handleClick);
    }, [session, notes, selectionPopover]);

    if (!session) return <>{children}</>;

    return (
        <>
            <div ref={contentRef} className="relative">
                {children}

                {selectionPopover && (
                    <ColorPicker
                        position={selectionPopover}
                        previewRange={selectionPopover.previewRange}
                        onPick={(color) => {
                            createHighlight(selectionPopover.text, color);
                            window.getSelection()?.removeAllRanges();
                            setSelectionPopover(null);
                        }}
                        onClose={() => {
                            CSS.highlights?.delete("note-preview");
                            setSelectionPopover(null);
                        }}
                    />
                )}

                {highlightPopover && (
                    <EditPopover
                        position={highlightPopover}
                        currentColor={highlightPopover.note.color || "yellow"}
                        onChangeColor={(color) => {
                            updateHighlightColor(highlightPopover.note.id, color);
                            setHighlightPopover(null);
                        }}
                        onDelete={() => {
                            deleteHighlight(highlightPopover.note.id);
                            setHighlightPopover(null);
                        }}
                        onClose={() => setHighlightPopover(null)}
                    />
                )}
            </div>
        </>
    );
}

// ---------------------------------------------------------------------------
// Color picker: just 4 circles, click = save
// ---------------------------------------------------------------------------
function ColorPicker({ position, previewRange, onPick, onClose }) {
    const [hoveredColor, setHoveredColor] = useState("yellow");

    // Show preview immediately
    useEffect(() => {
        if (!previewRange || typeof CSS === "undefined" || !CSS.highlights) return;
        setPreviewStyle("yellow");
        CSS.highlights.set("note-preview", new Highlight(previewRange));
    }, [previewRange]);

    // Update preview color on hover
    useEffect(() => {
        if (!previewRange || typeof CSS === "undefined" || !CSS.highlights) return;
        setPreviewStyle(hoveredColor);
        CSS.highlights.set("note-preview", new Highlight(previewRange));
    }, [hoveredColor, previewRange]);

    return (
        <>
            <div className="fixed inset-0 z-[58]" onClick={onClose} />
            <div
                data-note-popover
                className="absolute z-[59] bg-white dark:bg-bgDark-400 rounded-full shadow-xl border border-gray-200 dark:border-borderDark px-2.5 py-2 flex items-center gap-2"
                style={{ top: position.top, left: position.left }}
            >
                {COLORS.map((c) => (
                    <button
                        key={c.key}
                        onClick={() => onPick(c.key)}
                        onMouseEnter={() => setHoveredColor(c.key)}
                        className={`w-6 h-6 rounded-full ${c.bg} transition-all duration-150 hover:scale-125 active:scale-95`}
                    />
                ))}
            </div>
        </>
    );
}

// ---------------------------------------------------------------------------
// Edit popover: change color or delete (click active color = delete)
// ---------------------------------------------------------------------------
function EditPopover({ position, currentColor, onChangeColor, onDelete, onClose }) {
    return (
        <>
            <div className="fixed inset-0 z-[58]" onClick={onClose} />
            <div
                data-note-popover
                className="absolute z-[59] bg-white dark:bg-bgDark-400 rounded-full shadow-xl border border-gray-200 dark:border-borderDark px-2.5 py-2 flex items-center gap-2"
                style={{ top: position.top, left: position.left }}
            >
                {COLORS.map((c) => (
                    <button
                        key={c.key}
                        onClick={() => c.key === currentColor ? onDelete() : onChangeColor(c.key)}
                        className={`relative w-6 h-6 rounded-full ${c.bg} transition-all duration-150 hover:scale-125 active:scale-95`}
                    >
                        {c.key === currentColor && (
                            <svg xmlns="http://www.w3.org/2000/svg" className="absolute inset-0 m-auto w-3 h-3 text-gray-700/70" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        )}
                    </button>
                ))}
            </div>
        </>
    );
}
